<script setup lang="ts">
    import { computed, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import { useChatbotAuth } from '~/composables/useChatbotAuth'
    import { useChatbotProfileProvider } from '~/composables/useChatbotProfileProvider'
    import { useChatbotAgent } from '~/composables/useChatbotAgent'
    import { useChatbotAuthForm } from '~/composables/useChatbotAuthForm'
    import { useFocusTrapBypass } from '~/composables/useFocusTrapBypass'
    import { BACKEND_URL } from '~/utils'
    import {
        initializeAppInsights,
        setTelemetryContext,
        trackAppException,
    } from '~/modules/applicationInsights'
    import PkChatbot from 'components/src/chat/PkChatbot.vue'
    import PkChatbotAuth from 'components/src/chat/PkChatbotAuth.vue'
    import PkLoader from 'components/src/PkLoader.vue'
    import { useDialog } from 'composables'
    import { useWidgetEvents } from '~/utils/emitWidgetEvent'

    defineOptions({
        inheritAttrs: false,
    })

    useFocusTrapBypass()

    const props = withDefaults(
        defineProps<{
            agentId: string
            organizationSlug: string
            baseUrl?: string
            externalToken?: string
            awaitExternalToken?: boolean
            /** Comma-separated list of extra slot names to forward to PkChatbot (e.g. "tool-showArtifact,subagent"). */
            forwardedSlots?: string
            customCss?: string
            appinsightsConnectionString?: string
            /** Override the session/anonymous cookie name (defaults to 'eight-wave-ai'). */
            cookieName?: string
            /** Layout mode: `widget` (default, contained with header) or `fullscreen` (full-page app layout). */
            displayMode?: 'widget' | 'fullscreen'
        }>(),
        {
            baseUrl: BACKEND_URL,
            externalToken: undefined,
            awaitExternalToken: false,
            forwardedSlots: undefined,
            customCss: undefined,
            appinsightsConnectionString: undefined,
            cookieName: undefined,
            displayMode: 'widget',
        },
    )

    // dialog
    const { PkGlobalDialog } = useDialog()

    // Widget events (must be called during setup to capture the component instance)
    const { emit: emitWidgetEvent } = useWidgetEvents()

    // chatbot ref
    const chatbotEl = ref<InstanceType<typeof PkChatbot>>()

    // external context set by host application
    const externalContext = ref<Record<string, unknown> | undefined>()

    // Initialize Application Insights (singleton — first call wins)
    initializeAppInsights(props.appinsightsConnectionString)
    setTelemetryContext({
        organizationSlug: props.organizationSlug,
        agentId: props.agentId,
    })

    const {
        session,
        onError: onAuthError,
        headers,
        token,
        userId,
        signInWithEmail,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        signOut,
    } = useChatbotAuth({
        baseUrl: props.baseUrl,
        externalToken: () => props.externalToken,
        awaitExternalToken: props.awaitExternalToken,
        cookieName: props.cookieName,
    })

    // Enrich telemetry with userId once auth completes
    watch(userId, (id) => {
        if (id) {
            setTelemetryContext({ userId: id })
        }
    })

    // emit 'ready' once the session is established
    watch(
        () => session.value.data,
        (data) => {
            if (data) {
                emitWidgetEvent('ready')
            }
        },
    )

    function onError(error: { type: string; title: string; status: number }) {
        trackAppException(new Error(`${error.type}: ${error.title}`), {
            status: String(error.status),
        })
        emitWidgetEvent('error', {
            type: error.type,
            title: error.title,
            status: error.status,
        })
        onAuthError(error)
    }

    // i18n
    const { t } = useI18n({ useScope: 'global' })

    const {
        agent,
        branding,
        authMethods,
        requiresAuth,
        notFound,
        isLoading,
        reload,
        themeClass,
        themeStyle,
    } = useChatbotAgent({
        baseUrl: props.baseUrl,
        organizationSlug: () => props.organizationSlug,
        agentId: () => props.agentId,
        headers,
    })

    // Read-only profile context + voluntary sign-in affordance shared by both
    // widgets. Provides ChatbotProfileContextKey for the chat components.
    const { authBranding } = useChatbotProfileProvider({
        agent,
        branding,
        authMethods,
        session,
        signOut,
        token,
        baseUrl: props.baseUrl,
        headers,
        cookieName: props.cookieName,
    })

    // Login screen (private agents and the voluntary `view-auth` slot)
    const {
        authError,
        authSuccess,
        authLoading,
        handleSignIn,
        handleSignUp,
        handleReset,
        handleSocial,
    } = useChatbotAuthForm({
        signInWithEmail,
        signUpWithEmail,
        requestPasswordReset,
        signInWithSocialPopup,
        refetchSession: () => session.value.refetch(),
        reload,
    })

    const extraSlotNames = computed(() =>
        props.forwardedSlots
            ? props.forwardedSlots
                  .split(',')
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [],
    )

    // Greeting personalization: only real (non-anonymous) names
    const userName = computed(() =>
        session.value.data?.user?.isAnonymous
            ? undefined
            : session.value.data?.user?.name,
    )

    const mergedCss = computed(() => {
        const parts = [
            agent.value?.interface?.customCss,
            branding.value?.interface.customCss,
            props.customCss,
        ].filter(Boolean)
        return parts.length ? parts.join('\n') : undefined
    })

    // Public API exposed on the custom element DOM node
    defineExpose({
        sendMessage(text: string) {
            chatbotEl.value?.sendMessage(text)
        },
        startNewChat() {
            chatbotEl.value?.startNewChat()
        },
        setContext(context: Record<string, unknown>) {
            externalContext.value = context
        },
    })
</script>

<template>
    <!-- #region styles -->
    <component :is="'style'" v-if="mergedCss">{{ mergedCss }}</component>
    <!-- #endregion -->

    <!-- #region not found -->
    <div
        v-if="notFound"
        class="embedded-chat-widget embedded-chat-widget--not-found">
        <p>{{ t('message.agentNotFound') }}</p>
    </div>
    <!-- #endregion -->

    <!-- #region auth -->
    <Transition name="fade" appear>
        <div
            v-if="
                (agent || branding) &&
                !notFound &&
                requiresAuth &&
                (!session.data?.user?.id || session.data?.user?.isAnonymous)
            "
            class="embedded-chat-widget embedded-chat-widget--auth"
            :class="themeClass"
            :style="themeStyle">
            <PkChatbotAuth
                v-if="authMethods"
                v-model:error-message="authError"
                v-model:success="authSuccess"
                :branding="branding"
                :auth-methods="authMethods"
                :loading="authLoading"
                @sign-in="handleSignIn"
                @sign-up="handleSignUp"
                @request-reset="handleReset"
                @social="handleSocial" />
            <PkLoader v-else />
        </div>
    </Transition>
    <!-- #endregion -->

    <!-- #region loading -->
    <Transition name="fade" appear>
        <div
            v-if="isLoading && !notFound"
            class="embedded-chat-widget embedded-chat-widget--loading"
            :class="themeClass"
            :style="themeStyle">
            <PkLoader />
        </div>
    </Transition>
    <!-- #endregion -->

    <!-- #region chatbot -->
    <PkChatbot
        v-if="agent && session.data && !notFound"
        ref="chatbotEl"
        class="embedded-chat-widget"
        :class="themeClass"
        :style="themeStyle"
        :base-url
        :agent-id
        :user-id
        :name="agent.name"
        :agent-interface="agent.interface"
        :agent-file-upload="agent.capabilities?.fileUpload"
        :modifier="displayMode"
        :user-name="userName"
        :headers
        :actions="['upvote', 'downvote', 'feedback', 'regenerate']"
        :external-context="externalContext"
        @error="onError">
        <template #header-actions>
            <slot name="header-actions" />
        </template>
        <template #actions>
            <slot name="actions" />
        </template>
        <!-- Voluntary sign-in view for anonymous users (chat dropdown) -->
        <template #view-auth>
            <PkChatbotAuth
                v-model:error-message="authError"
                v-model:success="authSuccess"
                :branding="authBranding"
                :auth-methods="authMethods"
                :loading="authLoading"
                @sign-in="handleSignIn"
                @sign-up="handleSignUp"
                @request-reset="handleReset"
                @social="handleSocial" />
        </template>
        <template v-for="name in extraSlotNames" #[name]>
            <slot :name="name" />
        </template>
    </PkChatbot>
    <!-- #endregion -->

    <!-- #region global dialogs -->
    <PkGlobalDialog />
    <!-- #endregion -->
</template>

<style lang="scss">
    @use '~/assets/scss/style';

    .embedded-chat-widget {
        width: 100%;
        height: 100%;

        &--loading,
        &--not-found {
            display: flex;
            align-items: center;
            justify-content: center;
        }

        &--auth {
            display: flex;
            align-items: center;
            justify-content: center;
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.2s ease;
    }

    .fade-enter-from,
    .fade-leave-to {
        opacity: 0;
    }
</style>
